/**
 * ChatComposer — bottom-of-page input row for the chat screen.
 *
 * Ports Flutter `bottomInputActionsWidget` (`chat_screen.dart:3538-3565`) +
 * `inputWidget` (`:3568-3743`). Inline comments at each branch / call-site
 * carry the per-line Flutter citations and deviation notes; see in particular
 * the focus-tracking comment by `onFocus`/`onBlur`, the four branch comments,
 * and the inline notes on the add-media + gift buttons.
 *
 * The outer visibility predicate (`chat_screen.dart:3557-3565`) is pushed up
 * to the caller (`ChatPage`); this component takes `isSubscribedMutual` /
 * `isConversationBlocked` and self-hides accordingly. It does NOT gate on
 * first-load: the page already hides the whole composer behind its own loading
 * gate until `chatUsersInfo` resolves, so the composer mounts as soon as the
 * page is up — including the cold-deep-link window where `peerUser` is still
 * `null`. That is deliberate: it lets the single gift button render its
 * `peer-pending` status (gated/disabled) during the pre-resolution window and
 * flip to `ready` once the peer resolves, instead of unmounting the button for
 * the whole `peerUser === null` window (which used to make `peer-pending`
 * unobservable). The textarea + send button, however, are NOT exposed early:
 * they are gated on the hook's `isFirstLoad` (`inputDisabled`), which flips to
 * `false` only AFTER `initializeTextEncryptionUseCase` has derived the E2EE key
 * in `useChat.runInitialLoad`'s `finally`. This is a DIFFERENT (stricter) source
 * than the gift gate on purpose: `setPeerUser` precedes the awaited key
 * derivation, so a `peerUser`-only input gate would re-open the textarea while
 * the AES-GCM key is still null (encrypt silently passes through plaintext) —
 * gating on `isFirstLoad` keeps text un-sendable until E2EE is provably ready,
 * preserving the input half of Flutter's first-load gate
 * (`chat_screen.dart:3557-3565`, where `_isFirstLoad` flips inside `getData()`
 * strictly after `await _e2ee.generateKey(...)`). See the branch-2 comment and
 * the `inputDisabled` derivation below.
 * The add-media tap opens the chat add-content flow (`AddContentSheet.show`
 * with the page's `chatUsersInfo`) — Flutter `_sendMediaMessage`
 * (`chat_screen.dart:719-725`). The GIFT tap opens the send-gift sheet in chat
 * mode (`SendGiftSheet.show({ kind: SendGiftTargetType.chat, peerUser }, ...)`) — Flutter
 * `chat_screen.dart:3641-3681`, which builds the chat transaction from
 * `_currentUser!`/`_peerUser!` and calls `NavigationService.showSendGiftSheet`;
 * the web assembles that transaction in the Task-1 service (the sender comes
 * from auth state inside `useSendGift`), so the tap only needs `peerUser`. The
 * tap no-ops while `peerUser` is null (cold deep-link pre-resolution window) —
 * mirroring Flutter's `_peerUser!` guard. Both tap handlers + the gift
 * aria-label + the peer-pending status
 * token are threaded into `ChatMessageInput` (which renders the buttons) so
 * `AddContentSheet`/`SendGiftSheet`/`useLocale` stay here. l10n:
 * `chat.composer.{conversationBlocked,placeholder,giftAriaLabel}` +
 * `gift.sendGift` (sheet title, reused from the content flow).
 *
 * Deviations from Flutter (see inline comments for Flutter line cites):
 *  - Send + add-media + gift buttons are ALL always visible via the chat-local
 *    `ChatMessageInput`; Flutter hides the send button when unfocused and the
 *    add-media + gift buttons when focused (`Visibility(visible:
 *    !_focusNode.hasFocus, ...)` at `chat_screen.dart:3621-3683`). The web
 *    keeps all three right-side icons mounted regardless of focus — a
 *    deliberate web UI choice — so the focus-tracking state and the
 *    `trailingActions` slot were removed; `ChatMessageInput` renders the
 *    buttons inline.
 *  - `EdgeInsets.only(bottom: viewPadding.bottom)` collapse-on-focus is not
 *    ported — web inputs don't need the iOS safe-area dance.
 *  - "Conversation blocked" copy uses `responsive.font.lg` (16) instead of
 *    Flutter's literal 15 to stay on the project's responsive scale.
 */

import { useCallback, useRef } from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import {
  ChatMessageInput,
  type ChatMessageInputHandle,
} from './ChatMessageInput';
import { AddContentSheet } from '@/presentation/feature/publish/components/sheets/addContent/AddContentSheet';
import { canOpenAddContent } from '@/presentation/feature/publish/utils/canOpenAddContent';
import { SendGiftSheet } from '@/presentation/feature/gift/components/SendGiftSheet';
import { SendGiftTargetType } from '@/domain/enums/gift/sendGiftTargetType';
import { qaAttr } from '@/presentation/utils/qaAttr';
import type { ChatUsersInfoEntity } from '@/domain/entities/info/chatUsersInfoEntity';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import type { SendChatMediaArgs } from '../hooks/useChat';

export interface ChatComposerProps {
  /** Flutter `_subscribedToPeerExclusiveContent || _peerIsSubscribedToExclusiveContent` (`chat_screen.dart:3562-3563`). Render nothing when false — `SubscribeCTA` owns the surface. */
  isSubscribedMutual: boolean;
  /** Flutter `_currentUserConversationBlocked || _peerUserConversationBlocked` (`chat_screen.dart:3539`). Render the red "Conversation blocked" replacement when true. */
  isConversationBlocked: boolean;
  /** Send plaintext. Wired by host to `useChat.sendText`; draft clears synchronously after dispatch (Flutter `chat_screen.dart:659`). */
  onSendText: (text: string) => void;
  /** Send media (image / video). Wired by host to `useChat.sendMedia`; forwarded into `AddContentSheet.show` so the chat "Done" path inserts the optimistic "Processing" tile + runs the upload via the chat hook (Finding 3). Resolves the upload-success boolean so the sheet's submit hook can toast on failure. */
  onSendMedia: (args: SendChatMediaArgs) => Promise<boolean>;
  /** Resolved chat users info — forwarded to `AddContentSheet.show` so the add-media tap drives the chat-media flow (Flutter `_sendMediaMessage` passes `widget.chatUsersInfo`, `chat_screen.dart:719-725`). Always defined here: the page renders this composer only after its loading gate. */
  chatUsersInfo: ChatUsersInfoEntity;
  /** Resolved peer user — the gift recipient passed into the chat send-gift target. `null` only during the cold deep-link pre-resolution window; the gift tap no-ops while null (Flutter guards on `_peerUser!`, `chat_screen.dart:3651-3656`). The same null check drives the gift button's `peer-pending` qa-status token. (The sender — Flutter's `_currentUser!` — is sourced from auth state inside `useSendGift`, so it is not threaded here.) */
  peerUser: UserEntity | null;
  /** Hook's `isFirstLoad` (Flutter `_isFirstLoad`). Gates the textarea + send button: it flips to `false` only in `useChat.runInitialLoad`'s `finally`, i.e. AFTER `initializeTextEncryptionUseCase.execute(...)` has fully derived the E2EE key, so it is the only signal that guarantees encrypt-readiness. `peerUser` alone is NOT sufficient: `setPeerUser` precedes the awaited key derivation, leaving a window where the peer is resolved but the AES-GCM key is still `null` (encrypt would silently pass through plaintext). Drives `inputDisabled` only; the gift button's render eligibility / status stay on `peerUser === null`. */
  isFirstLoad: boolean;
  /** Screen key for `AddContentSheet.show` (add-media) and `SendGiftSheet.show` (gift) association. */
  screenKey: string;
}

export const ChatComposer: React.FC<ChatComposerProps> = ({
  isSubscribedMutual,
  isConversationBlocked,
  onSendText,
  onSendMedia,
  chatUsersInfo,
  peerUser,
  isFirstLoad,
  screenKey,
}) => {
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();
  const chat = l10n.chat;

  // Imperative handle to clear the input synchronously after dispatch,
  // matching Flutter `_textEditingController.clear()` (`chat_screen.dart:659`).
  const messageInputRef = useRef<ChatMessageInputHandle>(null);

  const handleSend = useCallback(
    (text: string): void => {
      const trimmed = text.trim();
      if (trimmed === '') return;
      onSendText(trimmed);
      messageInputRef.current?.clear();
    },
    [onSendText],
  );

  // Add-media tap → chat add-content flow. Ports Flutter `_sendMediaMessage`
  // (`chat_screen.dart:719-725`): navigate to the add-content flow passing
  // `chatUsersInfo`. On web that's `AddContentSheet.show(...)` with the `chat`
  // options (the resolved `chatUsersInfo` paired with its send action), which
  // resolves the chat flow (Drop + Preview only) and closes once on finish.
  // The upload-in-progress guard is the first thing Flutter's shared
  // `navigateToAddContentScreen` runs (`navigation_service.dart:420-435`), so it
  // applies to this call site too (`chat_screen.dart:721`): while a video upload
  // is running the info sheet shows and no add-content flow opens.
  const handleAddMedia = useCallback((): void => {
    if (!canOpenAddContent({ l10n, screenKey })) return;
    AddContentSheet.show({
      screenKey,
      width: responsive.addContentSheet.width,
      fixedHeight: responsive.addContentSheet.fixedHeight,
      chat: { chatUsersInfo, onSendMedia },
    });
  }, [l10n, screenKey, responsive.addContentSheet.width, responsive.addContentSheet.fixedHeight, chatUsersInfo, onSendMedia]);

  // GIFT tap → send-gift sheet in chat mode. Ports Flutter
  // `chat_screen.dart:3641-3681`: build the chat gift transaction from
  // `_currentUser!`/`_peerUser!` and open the send-gift sheet. The transaction
  // is assembled in the Task-1 `executeChatGift` service path, so the tap only
  // forwards `{ kind: SendGiftTargetType.chat, peerUser }`; the sender is read from auth state
  // inside `useSendGift`. No-ops while the peer is unresolved (cold deep-link
  // window) — Flutter force-unwraps `_peerUser!`, only reached once the peer is
  // loaded. The SAME null check drives `giftStatus` below, so the QA-observable
  // status and the real gate never disagree. The chat composer has no sheet
  // container ref, so the container arg is `undefined` (SheetService tolerates
  // it — see `SendGiftSheet.show`).
  const handleGiftTap = useCallback((): void => {
    if (peerUser === null) return;
    SendGiftSheet.show(
      { kind: SendGiftTargetType.chat, peerUser },
      l10n.gift.sendGift,
      undefined,
      screenKey,
    );
  }, [peerUser, l10n, screenKey]);

  // Single status token for the gift entry point, derived from the SAME
  // `peerUser == null` check that no-ops the handler so the QA-observable status
  // and the real gate stay in lockstep. `peer-pending` while the peer is
  // unresolved (cold deep-link window), `ready` once it resolves. Test-only
  // `data-qa-status`; l10n-exempt.
  const giftStatus = peerUser === null ? 'peer-pending' : 'ready';

  // Visual disabled affordance for the gift button, derived from the SAME
  // `peerUser == null` source as the no-op gate and `giftStatus` — dims +
  // natively disables the button during the `peer-pending` window so the
  // visible-but-inert tap reads as intentionally unavailable. The `onClick`
  // wiring and the `data-qa-status` token are untouched; the existing
  // `handleGiftTap` guard remains the functional gate.
  const giftDisabled = peerUser === null;

  // Gate the textarea + send button on `isFirstLoad` (the web mirror of
  // Flutter's `_isFirstLoad`, `chat_screen.dart:3557-3565`). This is the ONLY
  // signal that guarantees encrypt-readiness: it flips to `false` exclusively in
  // `useChat.runInitialLoad`'s `finally` block, which runs AFTER
  // `await initializeTextEncryptionUseCase.execute(...)` has fully derived the
  // AES-GCM key. `peerUser === null` is NOT sufficient here: `setPeerUser` fires
  // BEFORE the awaited key derivation in `runInitialLoad`, and that `await`
  // (plus the awaits inside `TextEncryptionService.initialize`) yields the event
  // loop, so React would flush the pending `setPeerUser` render — re-enabling the
  // input — while the key is still `null`. `encryptTextUseCase` runs in
  // pass-through (plaintext) mode while the key is null, and `sendText` only
  // gates on `groupChatId` (not the key), so a draft submitted in that gap would
  // reach Firestore UNENCRYPTED. Gating on `isFirstLoad` closes that window: the
  // textarea stays disabled and the send button no-ops (`ChatMessageInput`
  // disables send and the Enter-key path early-returns on `disabled`) until
  // E2EE is provably ready. The `peerUser === null` term is kept as a belt-and-
  // suspenders guard (and to read explicitly) but `isFirstLoad` is the binding
  // constraint. NOTE: this is deliberately a DIFFERENT source from the gift
  // gate — the gift button's render eligibility / `giftStatus` / `giftDisabled`
  // / `handleGiftTap` no-op stay on `peerUser === null` so `peer-pending` is
  // observable during the cold-deep-link window, while the input stays locked
  // through the slightly longer first-load window so it can never out-race E2EE.
  const inputDisabled = isFirstLoad || peerUser === null;

  // Branch 1: conversation-blocked replacement takes precedence over the
  // subscription gate (Flutter `chat_screen.dart:3539-3555`).
  if (isConversationBlocked) {
    return (
      <div
        {...qaAttr({ id: 'chat-composer', status: 'blocked' })}
        style={{
          width: '100%',
          // Flutter `DimensionConstants.bottomNavigationBarHeight`; the web
          // reuses `topBar.height` for top + bottom bars (see ChatAppBar).
          minHeight: responsive.topBar.height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: theme.background,
          boxSizing: 'border-box',
          paddingLeft: responsive.spacing.lg,
          paddingRight: responsive.spacing.lg,
        }}
      >
        <span
          style={{
            color: theme.activeRed,
            // Flutter literal 15 (`chat_screen.dart:3550`); web uses `lg` (16)
            // to stay on the responsive scale. Deliberate deviation.
            fontSize: responsive.font.lg,
            fontWeight: responsive.fontWeight.regular,
            textAlign: 'center',
          }}
        >
          {chat.composer.conversationBlocked}
        </span>
      </div>
    );
  }

  // Branch 2: no chrome when the subscribe CTA owns the surface
  // (`chat_screen.dart:3562-3565`).
  //
  // NOTE: the first-load gate (`isFirstLoad`) is intentionally NOT applied to
  // the gift button's render eligibility. The page already hides this whole
  // composer until `chatUsersInfo` resolves (its loading gate), so by the time
  // we get here the chat is past its loading screen. Keeping the composer
  // behind `isFirstLoad` as well used to unmount the gift button for the entire
  // `peerUser === null` cold-deep-link window — `isFirstLoad` only flips to
  // `false` AFTER `setPeerUser` in `useChat.runInitialLoad`, so the button
  // mounted directly as `ready` and the `peer-pending` guard window was never
  // observable (and could never be exercised). Mounting the composer here once
  // the page is up — driven purely by `peerUser === null` for the gift gate
  // below — commits `peer-pending` first, then flips to `ready` when the peer
  // resolves, so the QA-observable status, the `giftDisabled` affordance, and
  // the `handleGiftTap` no-op gate all share the single `peerUser === null`
  // source and never disagree. The textarea + send button are NOT inert by
  // accident — they are explicitly gated on the hook's `isFirstLoad` via
  // `inputDisabled` (see its derivation above), a STRICTER source than the gift
  // gate: `isFirstLoad` flips to `false` only after E2EE init completes in
  // `runInitialLoad`'s `finally`, whereas `peerUser` resolves earlier (before
  // the awaited key derivation). This is the web-specific handling of Flutter's
  // "mount the input only once the peer is loaded AND E2EE is ready"
  // (`chat_screen.dart:3557-3565`, where `_isFirstLoad` flips inside `getData()`
  // strictly after `await _e2ee.generateKey(...)`): the input half of that gate
  // is preserved via `inputDisabled` on `isFirstLoad` so text can never out-race
  // encryption, while the gift button is allowed to mount during the
  // `peerUser === null` window so its `peer-pending` status stays observable.
  if (!isSubscribedMutual) {
    return null;
  }

  // Branch 4: the default input row. `ChatMessageInput` owns the textarea,
  // the add-media + gift buttons, and the send button — all three right-side
  // icons stay mounted regardless of focus (web deviation; see file header).
  // The add-media tap (chat add-content flow) + gift tap (send-gift sheet, chat
  // variant) + gift status + gift aria-label are threaded in as props so
  // `AddContentSheet`/`SendGiftSheet`/`useLocale` stay here (Flutter
  // `chat_screen.dart:3592-3742`).
  return (
    <div
      {...qaAttr({ id: 'chat-composer', status: 'ready' })}
      style={{
        width: '100%',
        backgroundColor: theme.background,
        display: 'flex',
        alignItems: 'flex-end',
        // Flutter `EdgeInsets.only(left: 16.0, bottom: 4)` on the input;
        // we own the outer horizontal padding here so `ChatMessageInput`'s
        // built-in row doesn't double up.
        paddingLeft: responsive.spacing.lg,
        paddingRight: responsive.spacing.lg,
        paddingTop: responsive.spacing.xl,
        paddingBottom: responsive.spacing.xl,
        borderTop: `${responsive.border.thin}px solid ${theme.border}`,
        boxSizing: 'border-box',
      }}
    >
      <ChatMessageInput
        ref={messageInputRef}
        onSend={handleSend}
        disabled={inputDisabled}
        loading={false}
        placeholder={chat.composer.placeholder}
        onMediaTap={handleAddMedia}
        onGiftTap={handleGiftTap}
        giftStatus={giftStatus}
        giftAriaLabel={chat.composer.giftAriaLabel}
        giftDisabled={giftDisabled}
      />
    </div>
  );
};
